/**
 * AssessmentScoringModal – FHIR-native scoring settings for assessment templates.
 *
 * A comprehensive 4-tab modal scoped to the currently edited Questionnaire:
 *   1. Scoring Outputs   – define Observation outputs (total, subscales)
 *   2. Item Weights       – edit per-answer itemWeight extensions
 *   3. Interpretation Bands – severity ranges (maps to ObservationDefinition)
 *   4. Risk Flags         – item/score threshold triggers (maps to Flag resources)
 *
 * Supports mock-mode (preset data for PHQ-9, GAD-7, etc.) and live Medplum.
 */
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconPlus,
  IconTrash,
  IconAlertTriangle,
  IconTarget,
  IconScale,
  IconFlag,
  IconListDetails,
  IconSparkles,
  IconAlertCircle,
} from '@tabler/icons-react';
import type { QuestionnaireItem } from '@medplum/fhirtypes';
import type {
  ScoringConfiguration,
  ScoringOutput,
  InterpretationBand,
  RiskFlagTrigger,
  InstrumentPreset,
} from '../scoringTypes';
import {
  getBandColor,
  RISK_FLAG_COMPARISONS,
  INSTRUMENT_PRESETS,
  INSTRUMENT_PRESET_LABELS,
} from '../scoringTypes';
import type { ScoreCalculationMethod } from '../types';
import {
  getScoredItems,
  getItemWeight,
  getItemMaxWeight,
  computeMaxScore,
  updateItemOptionWeight,
  validateBands,
  generateScoringId,
} from '../utils/scoringUtils';
import { getAnswerOptionDisplayLabel } from '../utils/answerOptionLabel';
import {
  getPresetData,
  defaultTotalOutput,
} from '../data/mockScoringData';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AssessmentScoringModalProps {
  opened: boolean;
  onClose: () => void;
  items: QuestionnaireItem[];
  onItemsChange: (items: QuestionnaireItem[]) => void;
  scoreCalculationMethod: ScoreCalculationMethod;
  onScoreCalculationMethodChange: (v: ScoreCalculationMethod) => void;
  computedTotalScore: number | undefined;
  goodMax: number | '';
  onGoodMaxChange: (v: number | '') => void;
  okMax: number | '';
  onOkMaxChange: (v: number | '') => void;
  /** Current scoring configuration (loaded from questionnaire extension). */
  scoringConfig: ScoringConfiguration;
  onScoringConfigChange: (config: ScoringConfiguration) => void;
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const MONO_FF = 'var(--mantine-font-family-monospace)' as const;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AssessmentScoringModal({
  opened,
  onClose,
  items,
  onItemsChange,
  scoreCalculationMethod,
  onScoreCalculationMethodChange,
  computedTotalScore,
  goodMax: _goodMax,
  onGoodMaxChange,
  okMax: _okMax,
  onOkMaxChange,
  scoringConfig,
  onScoringConfigChange,
}: AssessmentScoringModalProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<string>('outputs');
  const [localConfig, setLocalConfig] = useState<ScoringConfiguration>(scoringConfig);

  useEffect(() => {
    if (opened) setLocalConfig(scoringConfig);
  }, [opened, scoringConfig]);

  const scoredItems = useMemo(() => getScoredItems(items), [items]);

  const syncLegacyRanges = useCallback(
    (config: ScoringConfiguration) => {
      const firstOutputId = config.outputs[0]?.id;
      if (!firstOutputId) return;
      const bands = config.bands[firstOutputId];
      if (!bands?.length) return;
      const sorted = [...bands].sort((a, b) => a.low - b.low);
      if (sorted.length >= 1) onGoodMaxChange(sorted[0].high);
      if (sorted.length >= 2) onOkMaxChange(sorted[1].high);
    },
    [onGoodMaxChange, onOkMaxChange],
  );

  const handleApply = useCallback(() => {
    onScoringConfigChange(localConfig);
    syncLegacyRanges(localConfig);
    onClose();
  }, [localConfig, onScoringConfigChange, onClose, syncLegacyRanges]);

  const handleLoadPreset = useCallback(
    (preset: InstrumentPreset) => {
      const data = getPresetData(preset);
      setLocalConfig(data.config);
      onItemsChange(data.items);
      if (data.config.outputs[0]?.transform === 'mean') {
        onScoreCalculationMethodChange('sum');
      }
    },
    [onItemsChange, onScoreCalculationMethodChange],
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconTarget size={20} style={{ color: 'var(--mantine-color-gold-6)' }} />
          <Title order={4}>Assessment Scoring Settings</Title>
        </Group>
      }
      size={900}
      centered
      closeOnClickOutside
      styles={{
        content: { border: '1px solid #9CA3AF', display: 'flex', flexDirection: 'column' },
        body: { padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        inner: { padding: 24 },
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v)}
        variant="default"
        style={{ flexShrink: 0 }}
      >
        <Tabs.List px="md">
          <Tabs.Tab value="outputs" leftSection={<IconListDetails size={15} />}>
            Scoring Outputs
          </Tabs.Tab>
          <Tabs.Tab value="weights" leftSection={<IconScale size={15} />}>
            Item Weights
          </Tabs.Tab>
          <Tabs.Tab value="bands" leftSection={<IconTarget size={15} />}>
            Interpretation Bands
          </Tabs.Tab>
          <Tabs.Tab value="flags" leftSection={<IconFlag size={15} />}>
            Risk Flags
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--mantine-color-gray-4) transparent',
        }}
        p="lg"
      >
        {activeTab === 'outputs' && (
          <ScoringOutputsTab
            config={localConfig}
            onChange={setLocalConfig}
            scoredItems={scoredItems}
            scoreCalculationMethod={scoreCalculationMethod}
            onLoadPreset={handleLoadPreset}
          />
        )}
        {activeTab === 'weights' && (
          <ItemWeightsTab
            items={items}
            onItemsChange={onItemsChange}
            scoredItems={scoredItems}
            computedTotalScore={computedTotalScore}
          />
        )}
        {activeTab === 'bands' && (
          <InterpretationBandsTab
            config={localConfig}
            onChange={setLocalConfig}
            items={items}
          />
        )}
        {activeTab === 'flags' && (
          <RiskFlagsTab
            config={localConfig}
            onChange={setLocalConfig}
            scoredItems={scoredItems}
          />
        )}
      </Box>

      <Group
        justify="flex-end"
        p="md"
        style={{ borderTop: '1px solid var(--mantine-color-gray-2)', flexShrink: 0 }}
      >
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply}>Apply Changes</Button>
      </Group>
    </Modal>
  );
}

// ===========================================================================
// Tab 1: Scoring Outputs
// ===========================================================================

function ScoringOutputsTab({
  config,
  onChange,
  scoredItems,
  scoreCalculationMethod,
  onLoadPreset,
}: {
  config: ScoringConfiguration;
  onChange: (c: ScoringConfiguration) => void;
  scoredItems: QuestionnaireItem[];
  scoreCalculationMethod: ScoreCalculationMethod;
  onLoadPreset: (preset: InstrumentPreset) => void;
}) {
  const [presetValue, setPresetValue] = useState<string | null>(null);

  const addOutput = () => {
    const out = defaultTotalOutput(config.outputs.length === 0 ? 'Total' : `Subscale ${config.outputs.length}`);
    onChange({ ...config, outputs: [...config.outputs, out] });
  };

  const removeOutput = (id: string) => {
    const bands = { ...config.bands };
    delete bands[id];
    onChange({
      ...config,
      outputs: config.outputs.filter((o) => o.id !== id),
      bands,
      riskFlags: config.riskFlags.filter((f) => f.scoreOutputId !== id),
    });
  };

  const updateOutput = (id: string, patch: Partial<ScoringOutput>) => {
    onChange({
      ...config,
      outputs: config.outputs.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    });
  };

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Define which score Observations the Bot will produce from completed QuestionnaireResponses.
        Each output maps to a FHIR Observation with a specific code and value type.
      </Text>

      <Paper withBorder p="md" style={{ backgroundColor: 'var(--mantine-color-gold-0)' }}>
        <Group gap="sm" align="flex-end" wrap="nowrap">
          <IconSparkles size={18} style={{ color: 'var(--mantine-color-gold-6)', flexShrink: 0, marginBottom: 4 }} />
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={600} mb={2}>Load from preset</Text>
            <Text size="xs" c="dimmed">
              Pre-fill scoring outputs, weights, bands, and risk flags from a standard instrument.
            </Text>
          </Box>
          <Select
            placeholder="Select instrument..."
            data={INSTRUMENT_PRESETS.map((p) => ({ value: p, label: INSTRUMENT_PRESET_LABELS[p] }))}
            value={presetValue}
            onChange={(v) => {
              if (v) {
                onLoadPreset(v as InstrumentPreset);
                setPresetValue(v);
              }
            }}
            clearable
            w={260}
            size="sm"
          />
        </Group>
      </Paper>

      <Paper withBorder p="md">
        <Group justify="space-between" mb="md">
          <Title order={4}>Score outputs</Title>
          <Badge variant="light" color="gold" size="sm">
            Method: {scoreCalculationMethod === 'sum' ? 'Sum' : scoreCalculationMethod}
          </Badge>
        </Group>

        {config.outputs.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <Text size="sm" c="dimmed">
              No scoring outputs defined. Add at least one to enable scoring.
            </Text>
            <Button variant="light" size="sm" leftSection={<IconPlus size={14} />} onClick={addOutput}>
              Add Total Score
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            {config.outputs.map((out) => (
              <Paper key={out.id} withBorder p="sm" radius="md" bg="gray.0">
                <Group gap="sm" align="flex-start" wrap="nowrap">
                  <Box style={{ flex: 1 }}>
                    <Group gap="xs" mb="xs" wrap="wrap">
                      <TextInput
                        value={out.label}
                        onChange={(e) => updateOutput(out.id, { label: e.currentTarget.value })}
                        size="sm"
                        placeholder="Label (e.g. Total Score)"
                        style={{ flex: 1, minWidth: 160 }}
                      />
                      <Select
                        value={out.transform}
                        onChange={(v) => v && updateOutput(out.id, { transform: v as 'sum' | 'mean' })}
                        data={[
                          { value: 'sum', label: 'Sum' },
                          { value: 'mean', label: 'Mean (sum / N)' },
                        ]}
                        size="sm"
                        w={150}
                        allowDeselect={false}
                      />
                      <Select
                        value={out.valueType}
                        onChange={(v) => v && updateOutput(out.id, { valueType: v as 'integer' | 'decimal' })}
                        data={[
                          { value: 'integer', label: 'Integer' },
                          { value: 'decimal', label: 'Decimal' },
                        ]}
                        size="sm"
                        w={120}
                        allowDeselect={false}
                      />
                    </Group>
                    <Group gap="xs" wrap="wrap">
                      <TextInput
                        value={out.codeSystem}
                        onChange={(e) => updateOutput(out.id, { codeSystem: e.currentTarget.value })}
                        size="xs"
                        placeholder="Code system (e.g. http://loinc.org)"
                        style={{ flex: 1, minWidth: 200 }}
                        styles={{ input: { fontSize: '0.75rem', fontFamily: MONO_FF } }}
                      />
                      <TextInput
                        value={out.codeValue}
                        onChange={(e) => updateOutput(out.id, { codeValue: e.currentTarget.value })}
                        size="xs"
                        placeholder="Code"
                        w={100}
                        styles={{ input: { fontSize: '0.75rem', fontFamily: MONO_FF } }}
                      />
                    </Group>
                    {out.includedItemLinkIds.length > 0 && (
                      <Text size="xs" c="dimmed" mt="xs">
                        Subscale: {out.includedItemLinkIds.length} items ({out.includedItemLinkIds.join(', ')})
                      </Text>
                    )}
                    {out.includedItemLinkIds.length === 0 && scoredItems.length > 0 && (
                      <Text size="xs" c="dimmed" mt="xs">
                        All {scoredItems.length} scored items &middot; Max: {computeMaxScore(scoredItems)}
                      </Text>
                    )}
                  </Box>
                  <Tooltip label="Remove output">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => removeOutput(out.id)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Paper>
            ))}
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconPlus size={14} />}
              onClick={addOutput}
              w="fit-content"
            >
              Add output
            </Button>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

// ===========================================================================
// Tab 2: Item Scoring Weights
// ===========================================================================

/** Single item row — memoized so expanding one item doesn't re-render siblings. */
const WeightItemRow = memo(function WeightItemRow({
  item,
  isExpanded,
  onToggle,
  items,
  onItemsChange,
}: {
  item: QuestionnaireItem;
  isExpanded: boolean;
  onToggle: (linkId: string) => void;
  items: QuestionnaireItem[];
  onItemsChange: (items: QuestionnaireItem[]) => void;
}) {
  const maxW = getItemMaxWeight(item);
  const hasWeights = item.answerOption?.some(
    (opt) => getItemWeight(opt) !== undefined,
  );

  const handleToggle = useCallback(() => onToggle(item.linkId), [onToggle, item.linkId]);

  return (
    <Box>
      <UnstyledButton
        onClick={handleToggle}
        w="100%"
        py="xs"
        px="sm"
        style={{
          borderBottom: isExpanded ? undefined : '1px solid var(--mantine-color-gray-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--mantine-spacing-xs)',
          borderRadius: isExpanded ? 'var(--mantine-radius-md) var(--mantine-radius-md) 0 0' : undefined,
          backgroundColor: isExpanded ? 'var(--mantine-color-gray-0)' : undefined,
        }}
      >
        {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        <Text size="sm" fw={500} style={{ flex: 1 }} lineClamp={1}>
          {item.text || item.linkId}
        </Text>
        <Badge variant="light" size="xs" color={hasWeights ? 'green' : 'gray'}>
          {hasWeights ? `max ${maxW}` : 'no weights'}
        </Badge>
        <Text size="xs" c="dimmed" ff={MONO_FF}>
          {item.linkId}
        </Text>
      </UnstyledButton>
      <Collapse in={isExpanded}>
        <Box
          px="md"
          pb="sm"
          pt="xs"
          style={{
            backgroundColor: 'var(--mantine-color-gray-0)',
            borderBottom: '1px solid var(--mantine-color-gray-2)',
            borderRadius: '0 0 var(--mantine-radius-md) var(--mantine-radius-md)',
          }}
        >
          <Table withRowBorders={false} verticalSpacing={4} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '60%' }}>
                  <Text size="xs" fw={600}>Answer option</Text>
                </Table.Th>
                <Table.Th style={{ width: '20%' }}>
                  <Text size="xs" fw={600}>Code</Text>
                </Table.Th>
                <Table.Th style={{ width: '20%' }}>
                  <Text size="xs" fw={600}>Weight</Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(item.answerOption ?? []).map((opt, optIdx) => {
                const display = getAnswerOptionDisplayLabel(opt) || `Option ${optIdx + 1}`;
                const code = opt.valueCoding?.code ?? '';
                const w = getItemWeight(opt);
                return (
                  <Table.Tr key={optIdx}>
                    <Table.Td>
                      <Text size="sm">{display}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" ff={MONO_FF}>{code}</Text>
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        value={w ?? ''}
                        onChange={(v) => {
                          const newWeight = typeof v === 'number' ? v : 0;
                          onItemsChange(
                            updateItemOptionWeight(items, item.linkId, optIdx, newWeight),
                          );
                        }}
                        min={0}
                        size="xs"
                        w={70}
                        placeholder="–"
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
});

function ItemWeightsTab({
  items,
  onItemsChange,
  scoredItems,
  computedTotalScore,
}: {
  items: QuestionnaireItem[];
  onItemsChange: (items: QuestionnaireItem[]) => void;
  scoredItems: QuestionnaireItem[];
  computedTotalScore: number | undefined;
}) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const handleToggle = useCallback((linkId: string) => {
    setExpandedItem((prev) => (prev === linkId ? null : linkId));
  }, []);

  if (scoredItems.length === 0) {
    return (
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          No scored items found. Add choice or open-choice items with answer options in the template
          editor, then configure their weights here.
        </Text>
        <Paper withBorder p="xl" ta="center">
          <Text size="sm" c="dimmed">
            Scored items will appear here once you add choice questions to the template.
          </Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Set numeric weights on each answer option using the standard FHIR{' '}
        <Text span size="sm" ff={MONO_FF} c="gray.7">itemWeight</Text>{' '}
        extension. The Bot computes scores by summing selected answer weights. Reverse-scoring or irregular
        scoring must be encoded directly in the option weights.
      </Text>

      <Paper withBorder p={0} radius="md">
        {scoredItems.map((item) => (
          <WeightItemRow
            key={item.linkId}
            item={item}
            isExpanded={expandedItem === item.linkId}
            onToggle={handleToggle}
            items={items}
            onItemsChange={onItemsChange}
          />
        ))}
      </Paper>

      <Divider />

      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Total max score
        </Text>
        <Badge variant="outline" size="lg" color="gold">
          {computedTotalScore ?? '–'}
        </Badge>
      </Group>
    </Stack>
  );
}

// ===========================================================================
// Tab 3: Interpretation Bands
// ===========================================================================

function InterpretationBandsTab({
  config,
  onChange,
  items,
}: {
  config: ScoringConfiguration;
  onChange: (c: ScoringConfiguration) => void;
  items: QuestionnaireItem[];
}) {
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(
    config.outputs[0]?.id ?? null,
  );

  useEffect(() => {
    if (!selectedOutputId && config.outputs.length > 0) {
      setSelectedOutputId(config.outputs[0].id);
    }
  }, [config.outputs, selectedOutputId]);

  const selectedOutput = useMemo(
    () => config.outputs.find((o) => o.id === selectedOutputId),
    [config.outputs, selectedOutputId],
  );
  const bands = useMemo(
    () => (selectedOutputId ? (config.bands[selectedOutputId] ?? []) : []),
    [config.bands, selectedOutputId],
  );

  const scoredItems = useMemo(() => getScoredItems(items), [items]);
  const includedIds = useMemo(
    () => (selectedOutput?.includedItemLinkIds.length ? selectedOutput.includedItemLinkIds : undefined),
    [selectedOutput],
  );
  const maxScore = useMemo(
    () => (selectedOutput ? computeMaxScore(scoredItems, includedIds) : undefined),
    [selectedOutput, scoredItems, includedIds],
  );

  const validation = useMemo(() => validateBands(bands, maxScore), [bands, maxScore]);

  const updateBands = useCallback((newBands: InterpretationBand[]) => {
    if (!selectedOutputId) return;
    onChange({
      ...config,
      bands: { ...config.bands, [selectedOutputId]: newBands },
    });
  }, [selectedOutputId, config, onChange]);

  const addBand = useCallback(() => {
    const lastHigh = bands.length > 0 ? bands[bands.length - 1].high + 1 : 0;
    const newBand: InterpretationBand = {
      id: generateScoringId('band'),
      label: '',
      low: lastHigh,
      high: maxScore ?? lastHigh + 10,
    };
    updateBands([...bands, newBand]);
  }, [bands, maxScore, updateBands]);

  const removeBand = useCallback((id: string) => {
    updateBands(bands.filter((b) => b.id !== id));
  }, [bands, updateBands]);

  const updateBand = useCallback((id: string, patch: Partial<InterpretationBand>) => {
    updateBands(bands.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, [bands, updateBands]);

  if (config.outputs.length === 0) {
    return (
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          Define scoring outputs first (in the Scoring Outputs tab) to configure interpretation bands.
        </Text>
        <Paper withBorder p="xl" ta="center">
          <Text size="sm" c="dimmed">
            Interpretation bands correspond to{' '}
            <Text span size="sm" ff={MONO_FF} c="gray.7">ObservationDefinition.qualifiedInterval</Text>{' '}
            in FHIR.
          </Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Define severity ranges for each scoring output. These map to FHIR{' '}
        <Text span size="sm" ff={MONO_FF} c="gray.7">ObservationDefinition.qualifiedInterval</Text>{' '}
        and determine the{' '}
        <Text span size="sm" ff={MONO_FF} c="gray.7">Observation.interpretation</Text>{' '}
        set by the Bot.
      </Text>

      {config.outputs.length > 1 && (
        <Select
          label="Score output"
          value={selectedOutputId}
          onChange={setSelectedOutputId}
          data={config.outputs.map((o) => ({ value: o.id, label: o.label }))}
          allowDeselect={false}
          w={300}
        />
      )}

      {selectedOutput && (
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {selectedOutput.label} &middot; {selectedOutput.transform === 'mean' ? 'Mean' : 'Sum'} &middot; Max: {maxScore ?? '–'}
          </Text>
        </Group>
      )}

      <Paper withBorder p="md">
        <Title order={4} mb="md">Severity bands</Title>

        {bands.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <Text size="sm" c="dimmed">
              No interpretation bands defined for this output.
            </Text>
            <Button variant="light" size="sm" leftSection={<IconPlus size={14} />} onClick={addBand}>
              Add first band
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            {bands.map((b, idx) => (
              <Paper key={b.id} withBorder p="sm" radius="md" bg="gray.0">
                <Group gap="sm" align="center" wrap="nowrap">
                  <Box
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: getBandColor(idx),
                    }}
                  />
                  <TextInput
                    value={b.label}
                    onChange={(e) => updateBand(b.id, { label: e.currentTarget.value })}
                    placeholder="Label (e.g. Minimal, Mild, Moderate...)"
                    size="sm"
                    style={{ flex: 1, minWidth: 140 }}
                  />
                  <Group gap={4} wrap="nowrap">
                    <NumberInput
                      value={b.low}
                      onChange={(v) => updateBand(b.id, { low: typeof v === 'number' ? v : 0 })}
                      size="sm"
                      w={75}
                      min={0}
                      label={idx === 0 ? 'Low' : undefined}
                    />
                    <Text c="dimmed" size="sm" mt={idx === 0 ? 24 : 0}>–</Text>
                    <NumberInput
                      value={b.high}
                      onChange={(v) => updateBand(b.id, { high: typeof v === 'number' ? v : 0 })}
                      size="sm"
                      w={75}
                      min={0}
                      label={idx === 0 ? 'High' : undefined}
                    />
                  </Group>
                  <Tooltip label="Remove band">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => removeBand(b.id)}
                      mt={idx === 0 ? 24 : 0}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Paper>
            ))}
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconPlus size={14} />}
              onClick={addBand}
              w="fit-content"
            >
              Add band
            </Button>
          </Stack>
        )}
      </Paper>

      {validation.errors.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" title="Validation errors">
          <Stack gap={2}>
            {validation.errors.map((err, i) => (
              <Text key={i} size="xs">{err}</Text>
            ))}
          </Stack>
        </Alert>
      )}
      {validation.warnings.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="Warnings">
          <Stack gap={2}>
            {validation.warnings.map((w, i) => (
              <Text key={i} size="xs">{w}</Text>
            ))}
          </Stack>
        </Alert>
      )}

      {bands.length > 0 && (
        <BandPreviewBar bands={bands} maxScore={maxScore} />
      )}
    </Stack>
  );
}

const PREVIEW_BAR_CONTAINER_STYLE = {
  display: 'flex',
  height: 28,
  borderRadius: 'var(--mantine-radius-sm)',
  overflow: 'hidden',
  border: '1px solid var(--mantine-color-gray-3)',
} as const;

/** Visual bar preview of interpretation bands. */
const BandPreviewBar = memo(function BandPreviewBar({ bands, maxScore }: { bands: InterpretationBand[]; maxScore?: number }) {
  const sorted = useMemo(() => [...bands].sort((a, b) => a.low - b.low), [bands]);
  const effectiveMax = maxScore ?? (sorted.length > 0 ? sorted[sorted.length - 1].high : 100);
  if (effectiveMax <= 0) return null;

  return (
    <Paper withBorder p="md" radius="md">
      <Text size="xs" fw={600} mb="xs" c="dimmed">
        Band preview
      </Text>
      <Box style={PREVIEW_BAR_CONTAINER_STYLE}>
        {sorted.map((b, idx) => {
          const width = ((b.high - b.low + 1) / (effectiveMax + 1)) * 100;
          return (
            <Tooltip
              key={b.id}
              label={`${b.label}: ${b.low}–${b.high}`}
              position="top"
            >
              <Box
                style={{
                  width: `${width}%`,
                  backgroundColor: getBandColor(idx),
                  opacity: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: idx < sorted.length - 1 ? '1px solid rgba(255,255,255,0.5)' : undefined,
                  cursor: 'default',
                }}
              >
                <Text size="xs" c="white" fw={600} style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                  {width > 8 ? b.label : ''}
                </Text>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Group gap="md" mt="xs">
        {sorted.map((b, idx) => (
          <Group key={b.id} gap={4}>
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getBandColor(idx),
              }}
            />
            <Text size="xs" c="dimmed">
              {b.label} ({b.low}–{b.high})
            </Text>
          </Group>
        ))}
      </Group>
    </Paper>
  );
});

// ===========================================================================
// Tab 4: Risk Flags
// ===========================================================================

function RiskFlagsTab({
  config,
  onChange,
  scoredItems,
}: {
  config: ScoringConfiguration;
  onChange: (c: ScoringConfiguration) => void;
  scoredItems: QuestionnaireItem[];
}) {
  const addFlag = () => {
    const flag: RiskFlagTrigger = {
      id: generateScoringId('flag'),
      type: 'item-threshold',
      threshold: 1,
      comparison: '>=',
      flagCode: '',
      flagDisplay: '',
    };
    onChange({ ...config, riskFlags: [...config.riskFlags, flag] });
  };

  const removeFlag = (id: string) => {
    onChange({ ...config, riskFlags: config.riskFlags.filter((f) => f.id !== id) });
  };

  const updateFlag = (id: string, patch: Partial<RiskFlagTrigger>) => {
    onChange({
      ...config,
      riskFlags: config.riskFlags.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });
  };

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Configure triggers that produce FHIR{' '}
        <Text span size="sm" ff={MONO_FF} c="gray.7">Flag</Text>{' '}
        resources for safety and risk alerts. The Bot evaluates these after scoring and creates{' '}
        <Text span size="sm" ff={MONO_FF} c="gray.7">Flag.subject = Patient</Text>{' '}
        with provenance references in{' '}
        <Text span size="sm" ff={MONO_FF} c="gray.7">Flag.note</Text>.
      </Text>

      <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={16} />}>
        <Text size="xs">
          Risk flags are safety-critical. Test thoroughly before enabling in production.
          No recommended actions are generated — flags are for alerting only.
        </Text>
      </Alert>

      <Paper withBorder p="md">
        <Title order={4} mb="md">Flag triggers</Title>

        {config.riskFlags.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <Text size="sm" c="dimmed">
              No risk flag triggers configured.
            </Text>
            <Button variant="light" size="sm" leftSection={<IconPlus size={14} />} onClick={addFlag}>
              Add risk flag trigger
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            {config.riskFlags.map((flag) => (
              <Paper key={flag.id} withBorder p="sm" radius="md" bg="gray.0">
                <Stack gap="xs">
                  <Group gap="sm" align="flex-start" wrap="nowrap">
                    <Box
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-red-5)',
                        flexShrink: 0,
                        marginTop: 6,
                      }}
                    />
                    <Box style={{ flex: 1 }}>
                      <Group gap="xs" mb="xs" wrap="wrap">
                        <Select
                          value={flag.type}
                          onChange={(v) => v && updateFlag(flag.id, { type: v as RiskFlagTrigger['type'] })}
                          data={[
                            { value: 'item-threshold', label: 'Item weight threshold' },
                            { value: 'score-threshold', label: 'Score output threshold' },
                          ]}
                          size="sm"
                          w={200}
                          allowDeselect={false}
                        />
                        {flag.type === 'item-threshold' && (
                          <Select
                            value={flag.itemLinkId ?? null}
                            onChange={(v) => updateFlag(flag.id, { itemLinkId: v ?? undefined })}
                            data={scoredItems.map((item) => ({
                              value: item.linkId,
                              label: item.text ? `${item.text.slice(0, 50)}${item.text.length > 50 ? '…' : ''}` : item.linkId,
                            }))}
                            placeholder="Select item..."
                            size="sm"
                            style={{ flex: 1, minWidth: 180 }}
                            searchable
                          />
                        )}
                        {flag.type === 'score-threshold' && (
                          <Select
                            value={flag.scoreOutputId ?? null}
                            onChange={(v) => updateFlag(flag.id, { scoreOutputId: v ?? undefined })}
                            data={config.outputs.map((o) => ({ value: o.id, label: o.label }))}
                            placeholder="Select output..."
                            size="sm"
                            style={{ flex: 1, minWidth: 180 }}
                          />
                        )}
                      </Group>
                      <Group gap="xs" wrap="wrap">
                        <Select
                          value={flag.comparison}
                          onChange={(v) => v && updateFlag(flag.id, { comparison: v as RiskFlagTrigger['comparison'] })}
                          data={RISK_FLAG_COMPARISONS}
                          size="sm"
                          w={180}
                          allowDeselect={false}
                        />
                        <NumberInput
                          value={flag.threshold}
                          onChange={(v) => updateFlag(flag.id, { threshold: typeof v === 'number' ? v : 0 })}
                          size="sm"
                          w={80}
                          min={0}
                          placeholder="Value"
                        />
                        <TextInput
                          value={flag.flagCode}
                          onChange={(e) => updateFlag(flag.id, { flagCode: e.currentTarget.value })}
                          size="sm"
                          placeholder="Flag code (e.g. self-harm-risk)"
                          style={{ flex: 1, minWidth: 160 }}
                        />
                      </Group>
                      <TextInput
                        value={flag.flagDisplay}
                        onChange={(e) => updateFlag(flag.id, { flagDisplay: e.currentTarget.value })}
                        size="sm"
                        placeholder="Flag display text (e.g. Risk flag: self-harm ideation)"
                        mt="xs"
                      />
                    </Box>
                    <Tooltip label="Remove flag">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => removeFlag(flag.id)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Stack>
              </Paper>
            ))}
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconPlus size={14} />}
              onClick={addFlag}
              w="fit-content"
            >
              Add risk flag trigger
            </Button>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
